import React, { useState, useEffect } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Menu, X } from "lucide-react";
import { useContent } from "../context/ContentContext";
import { scrambleTo } from "../lib/scramble";

const NAV_LINKS = [
  { label: "Home", to: "/" },
  { label: "About", to: "/about" },
  { label: "Portfolio", to: "/portfolio" },
  { label: "Contact", to: "/contact" },
];

export default function Header() {
  const location = useLocation();
  const { content } = useContent();
  const isHome = location.pathname === "/";
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setOpen(false);
  }, [location.pathname]);

  // On home the header floats over the hero video (white text). Elsewhere it's light gray with dark text.
  const overlay = isHome && !scrolled;
  const textColor = overlay ? "text-white" : "text-neutral-900";
  const bg = isHome
    ? scrolled
      ? "bg-[#141414]/90 backdrop-blur-md"
      : "bg-transparent"
    : "bg-[#d8d8d8]";

  return (
    <header
      className={`${isHome ? "fixed" : "relative"} top-0 left-0 right-0 z-50 ${bg}`}
      style={{ transition: "background-color 0.4s ease" }}
      data-testid="site-header"
    >
      <div className="max-w-[1500px] mx-auto flex items-center justify-between px-6 md:px-12 py-5">
        {/* Logo */}
        <Link to="/" className="relative inline-block group" data-testid="logo-link">
          <span
            className={`font-comfortaa font-medium tracking-[0.28em] text-lg md:text-xl ${isHome ? "text-white" : "text-neutral-900"}`}
          >
            {content.brand.namePrimary}
          </span>
          <span
            className="font-script text-2xl md:text-[27px] leading-none text-[#c56b7c] absolute -bottom-3 right-0 rotate-[-4deg]"
          >
            {content.brand.nameScript}
          </span>
        </Link>

        {/* Desktop nav */}
        <nav className="hidden md:flex items-center gap-9" data-testid="desktop-nav">
          {NAV_LINKS.map((l) => (
            <NavLink
              key={l.to}
              to={l.to}
              data-testid={`nav-${l.label.toLowerCase()}`}
              onMouseEnter={(e) => {
                const s = e.currentTarget.querySelector(".scramble");
                if (s) scrambleTo(s, l.label, { charTime: 30, perCharDelay: 2 });
              }}
              className={({ isActive }) =>
                `nav-link font-comfortaa text-[15px] ${isHome ? "text-white" : "text-neutral-900"} hover:opacity-80 ${
                  isActive ? "nav-active" : ""
                }`
              }
              style={{ transition: "opacity 0.25s ease" }}
            >
              <span className="scramble">{l.label}</span>
            </NavLink>
          ))}
        </nav>

        {/* Mobile burger */}
        <button
          className={`md:hidden ${textColor}`}
          onClick={() => setOpen(true)}
          aria-label="Open menu"
          data-testid="mobile-menu-button"
        >
          <Menu size={26} strokeWidth={1.5} />
        </button>
      </div>

      {/* Mobile fullscreen menu */}
      {open && (
        <div className="fixed inset-0 z-[60] bg-[#141414] flex flex-col" data-testid="mobile-menu">
          <div className="flex items-center justify-between px-6 py-5">
            <span className="font-comfortaa font-medium tracking-[0.28em] text-lg text-white">
              {content.brand.namePrimary}
            </span>
            <button onClick={() => setOpen(false)} className="text-white" aria-label="Close menu">
              <X size={26} strokeWidth={1.5} />
            </button>
          </div>
          <nav className="flex flex-col items-center justify-center flex-1 gap-8">
            {NAV_LINKS.map((l) => (
              <NavLink
                key={l.to}
                to={l.to}
                className="font-comfortaa text-2xl text-white hover:opacity-60"
                style={{ transition: "opacity 0.25s ease" }}
              >
                {l.label}
              </NavLink>
            ))}
          </nav>
        </div>
      )}
    </header>
  );
}
